import type { ReactElement } from 'react';
import type { Geography } from '@shared/types';

/**
 * A deterministic SVG geo visual — no external map tiles, no new dependencies.
 * Two modes, chosen from state alone:
 *   - ZIP targeting present -> a packed cloud of ZIP chips.
 *   - A store radius is set -> a radius circle with a center dot and a mile
 *     label; additional named areas (e.g. multiple locations) scatter as
 *     smaller satellite rings at fixed, deterministic angles - illustrative
 *     coverage, never a real map.
 * Renders a quiet placeholder when geography hasn't been set at all.
 */
const SIZE = 600;
const CENTER = SIZE / 2;

function ZipCloud({ zipCodes }: { zipCodes: string[] }): ReactElement {
  const codes = zipCodes.slice(0, 12);
  const cols = codes.length <= 4 ? 2 : 3;
  const cellW = SIZE / cols;
  const rows = Math.ceil(codes.length / cols);
  const cellH = Math.min(140, SIZE / Math.max(rows, 1));
  const totalH = cellH * rows;
  const offsetY = (SIZE - totalH) / 2;

  return (
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="geo-svg" role="img" aria-label="ZIP code targeting">
      {codes.map((zip, index) => {
        const col = index % cols;
        const row = Math.floor(index / cols);
        const cx = cellW * col + cellW / 2;
        const cy = offsetY + cellH * row + cellH / 2;
        const r = Math.min(cellW, cellH) * 0.34;
        return (
          <g key={zip}>
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill={index % 2 === 0 ? 'var(--doc-primary)' : 'var(--doc-accent)'}
              opacity={0.9}
            />
            <text x={cx} y={cy + 8} textAnchor="middle" className="geo-zip-label">
              {zip}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function RadiusMap({
  radiusMiles,
  centerLabel,
  satelliteCount,
}: {
  radiusMiles: number;
  centerLabel: string;
  /** Additional named areas beyond the primary one - drawn as satellite rings. */
  satelliteCount: number;
}): ReactElement {
  const primaryR = SIZE * 0.32;
  const satellites = Array.from({ length: Math.min(satelliteCount, 5) }, (_, index) => {
    const angle = (Math.PI * 2 * index) / Math.max(satelliteCount, 1) - Math.PI / 2;
    const orbit = primaryR * 1.35;
    const cx = CENTER + Math.cos(angle) * orbit * 0.55;
    const cy = CENTER + Math.sin(angle) * orbit * 0.55;
    return { cx, cy, r: primaryR * 0.28 };
  });

  return (
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="geo-svg" role="img" aria-label={`${radiusMiles} mile radius`}>
      {satellites.map((sat, index) => (
        <circle
          key={index}
          cx={sat.cx}
          cy={sat.cy}
          r={sat.r}
          fill="none"
          stroke="var(--doc-accent)"
          strokeWidth={3}
          opacity={0.55}
        />
      ))}
      <circle cx={CENTER} cy={CENTER} r={primaryR} fill="none" stroke="var(--doc-primary)" strokeWidth={4} />
      <circle cx={CENTER} cy={CENTER} r={primaryR * 0.62} fill="var(--doc-primary)" opacity={0.08} />
      <circle cx={CENTER} cy={CENTER} r={7} fill="var(--doc-accent)" />
      <text x={CENTER} y={CENTER - 18} textAnchor="middle" className="geo-center-label">
        {centerLabel}
      </text>
      <text x={CENTER} y={CENTER + 34} textAnchor="middle" className="geo-mile-label">
        {radiusMiles} mi radius
      </text>
    </svg>
  );
}

export function GeoSchematic({
  geography,
  centerLabel,
  compact,
}: {
  geography: Geography;
  centerLabel: string;
  /** A smaller decorative treatment (e.g. the product_plan visual slot). */
  compact?: boolean;
}): ReactElement {
  if (geography.zipCodes.length > 0) {
    return (
      <div className={`geo-schematic${compact ? ' geo-schematic-compact' : ''}`}>
        <ZipCloud zipCodes={geography.zipCodes} />
      </div>
    );
  }
  if (typeof geography.storeRadiusMiles === 'number') {
    return (
      <div className={`geo-schematic${compact ? ' geo-schematic-compact' : ''}`}>
        <RadiusMap
          radiusMiles={geography.storeRadiusMiles}
          centerLabel={centerLabel}
          satelliteCount={Math.max(0, geography.areas.length - 1)}
        />
      </div>
    );
  }
  return (
    <div className={`geo-schematic geo-schematic-empty${compact ? ' geo-schematic-compact' : ''}`}>
      <span>Coverage area pending</span>
    </div>
  );
}
